import React, { useState } from 'react';
import api from '../../services/api';

function ExcelUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please select a valid .xlsx file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload/leads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResults(response.data.results);
      setFile(null);
      // Reset file input
      document.getElementById('file-input').value = '';
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create a sample Excel template
    const templateData = [
      {
        schoolName: 'Example School',
        personName: 'John Smith',
        designation: 'Principal',
        email: 'contact@example.com',
        address: '123 Main St',
        phoneNumber: '+1234567890',
        source: 'Website',
        expectedValue: 50000,
        progressStatus: 'Prospecting',
        statusDescription: 'Initial contact made',
        progressPlannedEndDate: '2024-03-01',
        startDate: '2024-01-01',
        endDate: '2024-06-01'
      }
    ];

    const csvContent = [
      'schoolName,personName,designation,email,address,phoneNumber,source,expectedValue,progressStatus,statusDescription,progressPlannedEndDate,startDate,endDate',
      templateData.map(row => Object.values(row).join(',')).join('\n')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-6">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-green-600 bg-clip-text text-transparent">
              Excel Upload
            </h2>
            <p className="text-gray-600 mt-1">Bulk import leads from Excel file</p>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">Upload Instructions</h3>
             <div className="text-sm text-blue-700 mb-3">
            <p>• Names should be exactly same, capital as well as small letters should be same too.</p>
            <p>• Upload .xlsx files only</p>
            <p>• Leads without assignees will be available for all users to pick up</p>
            <p>• Duplicate emails or phone numbers will be skipped</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">Required Fields:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• schoolName (School/Organization name)</li>
                <li>• email (Contact email address)</li>
                <li>• address (Full address)</li>
                <li>• phoneNumber (Contact phone)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">Optional Fields:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• personName (Contact person name)</li>
                <li>• designation (Job title/position)</li>
                <li>• source (Lead source)</li>
                <li>• expectedValue (Deal value)</li>
                <li>• progressStatus (Current status) [Prospecting, Qualified, Demo Scheduled / Done, Evaluation / Pilot, Decision, Closed Won, Closed Lost]</li>
                <li>• statusDescription (Description for current status, sort of remarks)</li>
                <li>• progressPlannedEndDate (Status deadline)</li>
                <li>• startDate (Lead engagement start)</li>
                <li>• endDate (Target conversion date)</li>
              </ul>
            </div>
          </div>
       
          <button
            onClick={downloadTemplate}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition text-sm"
          >
            Download Complete Template
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* File Upload */}
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center mb-6">
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <input
                id="file-input"
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="file-input"
                className="cursor-pointer bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition inline-block"
              >
                Choose Excel File
              </label>
            </div>
            {file && (
              <div className="text-sm text-gray-600">
                Selected: {file.name}
              </div>
            )}
          </div>
        </div>

        {/* Upload Button */}
        <div className="flex justify-center mb-6">
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className={`px-8 py-3 rounded-lg font-medium transition ${
              !file || uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600'
            }`}
          >
            {uploading ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Uploading...</span>
              </div>
            ) : (
              'Upload Leads'
            )}
          </button>
        </div>

        {/* Results */}
        {results && (
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Upload Results</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium text-green-800">Successful</span>
                </div>
                <div className="text-2xl font-bold text-green-600 mt-1">{results.success}</div>
              </div>
              <div className="bg-red-100 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium text-red-800">Failed</span>
                </div>
                <div className="text-2xl font-bold text-red-600 mt-1">{results.failed}</div>
              </div>
            </div>

            {results.errors && results.errors.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-800 mb-2">Errors:</h4>
                <div className="bg-white border rounded-lg p-4 max-h-60 overflow-y-auto">
                  {results.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-600 py-1 border-b border-gray-100 last:border-b-0">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExcelUpload;